import type { DateRange } from "react-day-picker";

import {
  addDays,
  addMonths,
  addWeeks,
  format,
  isSameDay,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
  subWeeks,
} from "date-fns";

import { currentLanguageLocale } from "@/i18n/getDateFnsLocale.ts";

// Status options for filtering
export const statusOptions = [
  { id: "all", label: "allStatuses" },
  { id: "01F8MECHZX3TBDSZ7K9QDYFFJA", label: "created" },
  { id: "01F8MECHZX3TBDSZ7K9QDYFFJB", label: "paid" },
  { id: "01F8MECHZX3TBDSZ7K9QDYFFJC", label: "inProgress" },
  { id: "01F8MECHZX3TBDSZ7K9QDYFFJD", label: "sessionFinished" },
  { id: "01F8MECHZX3TBDSZ7K9QDYFFJE", label: "completed" },
];

// Get selected status label
export const getSelectedStatusLabel = (statusFilter: string) => {
  const option = statusOptions.find((option) => option.id === statusFilter);

  return option ? option.label : "allStatuses";
};

// Handle date preset selection
export const getDateRangeFromPreset = (preset: string): DateRange => {
  const today = new Date();
  let from = today;
  let to = today;

  switch (preset) {
    case "today":
      to = today;
      break;
    case "tomorrow":
      from = addDays(today, 1);
      to = addDays(today, 1);
      break;
    case "thisWeek":
      from = startOfWeek(today, { weekStartsOn: 1 });
      to = addDays(startOfWeek(today, { weekStartsOn: 1 }), 6);
      break;
    case "nextWeek":
      from = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      to = addDays(startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 }), 6);
      break;
    case "thisMonth":
      from = startOfMonth(today);
      to = addDays(startOfMonth(addMonths(today, 1)), -1);
      break;
    case "nextMonth":
      from = startOfMonth(addMonths(today, 1));
      to = addDays(startOfMonth(addMonths(today, 2)), -1);
      break;
    case "last7Days":
      from = subDays(today, 7);
      to = subDays(today, 1);
      break;
    case "last30Days":
      from = subDays(today, 30);
      to = subDays(today, 1);
      break;
    case "yesterday":
      from = subDays(today, 1);
      to = subDays(today, 1);
      break;
    case "lastWeek":
      from = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
      to = addDays(startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 }), 6);
      break;
    case "lastMonth":
      from = startOfMonth(subMonths(today, 1));
      to = addDays(startOfMonth(today), -1);
      break;
  }

  return { from, to };
};

// Get current date preset based on range
export const getCurrentDatePreset = (dateRange: DateRange): string => {
  if (!dateRange.from || !dateRange.to) return "";

  const today = startOfDay(new Date());
  const from = startOfDay(dateRange.from);
  const to = startOfDay(dateRange.to);
  const yesterday = startOfDay(subDays(today, 1));

  if (isSameDay(from, today) && isSameDay(to, today)) return "today";
  if (isSameDay(from, addDays(today, 1)) && isSameDay(to, addDays(today, 1))) return "tomorrow";
  if (isSameDay(from, yesterday) && isSameDay(to, yesterday)) return "yesterday";

  const thisWeekStart = startOfWeek(today, { weekStartsOn: 1 });
  const thisWeekEnd = addDays(thisWeekStart, 6);

  if (isSameDay(from, thisWeekStart) && isSameDay(to, thisWeekEnd)) return "thisWeek";

  const nextWeekStart = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
  const nextWeekEnd = addDays(nextWeekStart, 6);

  if (isSameDay(from, nextWeekStart) && isSameDay(to, nextWeekEnd)) return "nextWeek";

  const lastWeekStart = startOfWeek(subWeeks(today, 1), { weekStartsOn: 1 });
  const lastWeekEnd = addDays(lastWeekStart, 6);

  if (isSameDay(from, lastWeekStart) && isSameDay(to, lastWeekEnd)) return "lastWeek";

  const thisMonthStart = startOfMonth(today);
  const thisMonthEnd = addDays(startOfMonth(addMonths(today, 1)), -1);

  if (isSameDay(from, thisMonthStart) && isSameDay(to, thisMonthEnd)) return "thisMonth";

  const nextMonthStart = startOfMonth(addMonths(today, 1));
  const nextMonthEnd = addDays(startOfMonth(addMonths(today, 2)), -1);

  if (isSameDay(from, nextMonthStart) && isSameDay(to, nextMonthEnd)) return "nextMonth";

  const lastMonthStart = startOfMonth(subMonths(today, 1));
  const lastMonthEnd = addDays(startOfMonth(today), -1);

  if (isSameDay(from, lastMonthStart) && isSameDay(to, lastMonthEnd)) return "lastMonth";

  if (isSameDay(from, subDays(today, 7)) && isSameDay(to, subDays(today, 1))) return "last7Days";
  if (isSameDay(from, subDays(today, 30)) && isSameDay(to, subDays(today, 1))) return "last30Days";

  return "";
};

// Get date preset label
export const getDatePresetLabel = (dateRange: DateRange, presetType: string, t: any): string => {
  switch (presetType) {
    case "today":
      return t("common:today", "Today");
    case "tomorrow":
      return t("common:tomorrow", "Tomorrow");
    case "yesterday":
      return t("common:yesterday", "Yesterday");
    case "thisWeek":
      return t("common:thisWeek", "This Week");
    case "nextWeek":
      return t("common:nextWeek", "Next Week");
    case "lastWeek":
      return t("common:lastWeek", "Last Week");
    case "thisMonth":
      return t("common:thisMonth", "This Month");
    case "nextMonth":
      return t("common:nextMonth", "Next Month");
    case "lastMonth":
      return t("common:lastMonth", "Last Month");
    case "last7Days":
      return t("common:last7Days", "Last 7 Days");
    case "last30Days":
      return t("common:last30Days", "Last 30 Days");
    default:
      return `${format(dateRange.from as Date, "MMM d", { locale: currentLanguageLocale() })} - ${format(dateRange.to as Date, "MMM d, yyyy", { locale: currentLanguageLocale() })}`;
  }
};

// Format date parameters for API request
export const formatDateParamsForApi = (
  dateRange: DateRange,
  statusFilter: string,
  searchQuery?: string,
) => {
  return {
    dateFrom: dateRange.from
      ? format(dateRange.from, "yyyy-MM-dd")
      : format(new Date(), "yyyy-MM-dd"),
    dateTo: dateRange.to
      ? format(dateRange.to, "yyyy-MM-dd")
      : format(addDays(new Date(), 7), "yyyy-MM-dd"),
    status: statusFilter === "all" ? undefined : statusFilter,
    search: searchQuery || undefined,
  };
};

// Format lesson time display
export const formatLessonTime = (startTime?: string, endTime?: string) => {
  if (!startTime) return "";

  const formattedStart = format(parseISO(startTime), "HH:mm");
  const formattedEnd = endTime ? format(parseISO(endTime), "HH:mm") : "";

  return `${formattedStart} - ${formattedEnd}`;
};

// Format lesson date display
export const formatLessonDate = (startTime?: string) => {
  if (!startTime) return "";

  return format(parseISO(startTime), "d MMM, yyyy");
};
